<script setup lang="ts">
import type { ServiceItem } from '~/components/OurServices.vue';
import { computed } from 'vue';

const route = useRoute();
const path = route.path;

const { $axios } = useNuxtApp();

type yaostData = {
    seo_title?: string;
    seo_description?: string;
    opengraph_title?: string;
    opengraph_description?: string;
    opengraph_image?: string;
    twitter_title?: string;
    twitter_description?: string;
    twitter_image?: string;
    canonical?: string;
    robots_noindex?: boolean;
    robots_nofollow?: boolean;
    focus_keyword?: string;
};

type PageData = {
    page: {
        title?: string;
        template?: string;
        excerpt?: string;
        opengraph_image_url?: string;
        link?: string;
        yoast_seo?: yaostData;
        fields: {
            sections: {
                acf_fc_layout: string;
            }[];
            seo_title: string;
            seo_description: string;
        };
    };
    single_post_services?: ServiceItem[];
};

const { data, error, pending } = useAsyncData(path, () =>
    $axios.get(path, {
        params: {
            get_json_data: 'page',
        },
    })
);

console.info('Page pull', path, data.value, error.value, pending.value);

const getSectionComponent = (name: string) =>
    defineAsyncComponent({
        loader: () => import(`~/components/${name}.vue`),
        timeout: 3000,
    });

const decodeHtml = (input: string): string => {
    return input
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
};

const dataNonPage = ref({
    section: {
        heading: '404',
        text: `We can't find the page you're looking for`,
        button: {
            url: '/',
            title: 'GO HOME',
            target: '_self',
        },
    },
});

const pageTitle = computed(() =>
    decodeHtml(
        data.value?.page?.yoast_seo?.seo_title || data.value?.page?.title || path
    )
);

const title = computed(() =>
    decodeHtml(data.value?.page?.yoast_seo?.seo_title || '')
);
const description = computed(
    () => data.value?.page?.yoast_seo?.seo_description
);
const ogImage = computed(
    () =>
        data.value?.page?.yoast_seo?.opengraph_image ||
        data.value?.page?.opengraph_image_url ||
        '/img/share.png'
);
const ogUrl = computed(() => data.value?.page?.link || '');
const focusKeyword = computed(() => data.value?.page?.yoast_seo?.focus_keyword);

useHead({
    title: pageTitle,
    meta: [
        { name: 'description', content: description },
        { name: 'keywords', content: focusKeyword },
        {
            property: 'og:title',
            content: title,
        },
        {
            property: 'og:description',
            content: description,
        },
        { property: 'og:image', content: ogImage },
        { property: 'og:url', content: ogUrl },
        {
            name: 'twitter:title',
            content: title,
        },
        {
            name: 'twitter:description',
            content: description,
        },
        {
            name: 'twitter:image',
            content: ogImage,
        },
        { name: 'twitter:card', content: 'summary_large_image' },
    ],
});
</script>

<template>
    <template v-if="error">
        <NonPage :section="dataNonPage.section" />
    </template>
    <template v-else>
        <Transition mode="out-in" :css="false">
            <Suspense>
                <template #default>
                    <template v-if="path?.match(/^\/blog\/[^/]+/)">
                        <PostDetail v-if="data?.page" :post="data?.page" :services="data?.single_post_services" />
                    </template>
                    <template v-else-if="path?.includes('/team/')">
                        <TeamDetail v-if="data?.page" :post="data?.page" :services="data?.single_post_services" />
                    </template>
                    <template v-else-if="path">
                        <template v-if="data?.page?.template === 'template-modules'">
                            <div>
                                <component :is="getSectionComponent(section.acf_fc_layout)"
                                    v-for="(section, index) in data?.page?.fields?.sections"
                                    :key="`${section.acf_fc_layout}-${index}`" :section="section" />
                            </div>
                        </template>
                        <template v-else>
                            <PostDetail v-if="data?.page" :post="data?.page" :services="data?.single_post_services" />
                        </template>
                    </template>
                </template>
            </Suspense>
        </Transition>
    </template>
</template>
